import type { InstructionStep, Language, Recipe } from '../types';
import { getTranslation } from './translations';
import { formatIngredientQuantity } from './unitConversion';

type RecipeTextSource = Pick<Recipe, 'name' | 'servings' | 'prepTime' | 'ingredients' | 'instructions' | 'originNote'>;

export function formatRecipeText(
    recipe: RecipeTextSource,
    t: (key: Parameters<typeof getTranslation>[1]) => string,
): string {
    let text = `${recipe.name || 'Recipe'}\n`;
    text += `${'='.repeat(Math.max(10, (recipe.name || '').length))}\n\n`;

    text += `${t('servings')}: ${recipe.servings || '-'} | ${t('prep')}: ${recipe.prepTime || '-'} ${t('mins')}\n\n`;

    text += `${t('ingredients').toUpperCase()}\n`;
    text += `${'-'.repeat(t('ingredients').length)}\n`;

    const ingredientsBySection: Record<string, string[]> = {};
    const defaultSection = t('general');

    (recipe.ingredients || []).forEach((ing) => {
        const section = ing.section || defaultSection;
        if (!ingredientsBySection[section]) ingredientsBySection[section] = [];
        let line = `${formatIngredientQuantity(ing.quantity, ing.unit, t)} ${ing.name}`;
        if (ing.alternatives?.length) {
            const altText = ing.alternatives
                .map((alt) => `${formatIngredientQuantity(alt.quantity, alt.unit, t)} ${alt.name}`)
                .join('; ');
            line += ` (${altText})`;
        }
        ingredientsBySection[section].push(line);
    });

    Object.entries(ingredientsBySection).forEach(([section, items]) => {
        if (section !== defaultSection || Object.keys(ingredientsBySection).length > 1) {
            text += `\n[${section}]\n`;
        }
        items.forEach((item) => {
            text += `- ${item}\n`;
        });
    });

    text += `\n${t('instructions').toUpperCase()}\n`;
    text += `${'-'.repeat(t('instructions').length)}\n`;

    if (typeof recipe.instructions === 'string') {
        text += recipe.instructions;
    } else if (Array.isArray(recipe.instructions)) {
        (recipe.instructions as InstructionStep[]).forEach((step, idx) => {
            if (step.type === 'header') {
                text += `\n[${step.content}]\n`;
            } else {
                text += `${idx + 1}. ${step.content}\n`;
            }
        });
    }

    if (recipe.originNote) {
        text += `\n\nNotes: ${recipe.originNote}`;
    }

    return text;
}

export function formatRecipeTextFromLanguage(recipe: RecipeTextSource, language?: Language): string {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    return formatRecipeText(recipe, t);
}
